'use client';

/**
 * FreebieForm.tsx
 *
 * Two-step capture form for the freebie landing-page template.
 * Wave-3 Station 3 — 2026-05-10 (Vela)
 * Cycle 15 update — 2026-05-11 (Vela):
 *   Change A: V2 confirmation card — copy-email / copy-subject fallback buttons.
 *   When the mailto: link silently fails (webmail-in-browser / no default mail app),
 *   two ghost buttons let the visitor copy the address and subject to clipboard manually.
 *   - "Copy email" / "Copy subject" — 1.8s "Copied ✓" success state, then revert.
 *   - Graceful degradation: if Clipboard API unavailable, inline fallback text.
 *   - Accessibility: aria-label on each button, focus ring via --focus-ring.
 *
 * Architecture:
 *   - Step 1: email field + Mailcheck layer (same as newsletter form)
 *   - Step 2: name field, slides in via translateY animation, card height fixed
 *   - Done V1: confirmation card — envelope icon, "Check your inbox"
 *   - Done V2: confirmation card — person icon, "Looks like you already have it"
 *
 * Submit logic:
 *   POSTs to /api/freebie with { email, firstName, ghlTag, website (honeypot) }
 *   Response branches:
 *     'freebie-sent'        → Confirmation V1 (new contact or existing without tag)
 *     'already-has-freebie' → Confirmation V2 (existing contact already has the tag)
 *     'error'               → friendly retry message, form stays on name step
 *
 * GHL tag-existence check is server-side in /api/freebie/route.ts.
 * This client component never sees GHL credentials.
 *
 * Motion spec (Aria spec §15.4):
 *   Email exit:  translateY(0→-8px) + opacity(1→0), 300ms ease-out
 *   Name entry:  translateY(8px→0) + opacity(0→1), 300ms ease-out, 60ms delay
 *   Card swap:   opacity(1→0→1), 400ms ease-out
 *   prefers-reduced-motion: swap instantly at full opacity, no transforms
 *
 * Accessibility:
 *   - Focus moves to name input on step 2 (Aria spec §15.4)
 *   - prefers-reduced-motion honored via matchMedia check
 *   - Labels: visible label on name field; email uses aria-label
 *   - Back link: "← change email" returns to step 1 without clearing email
 *
 * CSS: all classes in globals.css §freebie-form (below newsletter section)
 * Kit tokens: --freebie-form-bg, --border-field-on-dark, --cta, --gold, --text,
 *             --text-muted, --radius-lg, --radius-md, --radius-sm, --space-*,
 *             --motion-duration-medium, --motion-duration-entry, --motion-easing-ease-out
 *
 * DO NOT modify the newsletter submit path — this form uses a separate /api/freebie route.
 * DO NOT share state between this component and NewsletterForm.tsx.
 */

import { useEffect, useRef, useState } from 'react';
import { useMailcheck } from '@/hooks/useMailcheck';
import { useTurnstile } from '@/hooks/useTurnstile';
import OrbitSpinner from '@/components/OrbitSpinner';
import { ga4Event } from '@/lib/analytics/ga4';

// ---------------------------------------------------------------------------
// Icon components (inline SVG — per kit rule; no <img src="*.svg"> for color-flexible icons)
// ---------------------------------------------------------------------------

const EnvelopeIcon = () => (
  <svg
    width={32} height={32}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M2 7l10 7 10-7" />
  </svg>
);

const PersonIcon = () => (
  <svg
    width={32} height={32}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

// ---------------------------------------------------------------------------
// Inline email-link renderer
// Splits a string at a mailto: address and renders the address as a link.
// Used for the V2 confirmation body only.
//
// Convention (cycle 7): the V2 confirmation re-send address is always
// mail@pianofantasy.com — never support@. Update any freebie instance that
// still references support@ when it goes through its full pipeline cycle.
// ---------------------------------------------------------------------------

const RESEND_EMAIL = 'mail@pianofantasy.com';

function renderWithEmailLink(text: string, subject?: string) {
  const idx = text.indexOf(RESEND_EMAIL);
  if (idx === -1) return <>{text}</>;
  const href = subject
    ? `mailto:${RESEND_EMAIL}?subject=${encodeURIComponent(subject)}`
    : `mailto:${RESEND_EMAIL}`;
  return (
    <>
      {text.slice(0, idx)}
      <a
        href={href}
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        {RESEND_EMAIL}
      </a>
      {text.slice(idx + RESEND_EMAIL.length)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FreebieFormProps {
  /** Exact GHL tag string for this freebie instance. e.g. "[FREE PDF] umbrella technique" */
  ghlTag: string;
  /** V1 body copy — shown after new-contact signup. Hazel finalizes. */
  confirmationV1Body: string;
  /** V2 body copy — shown when contact already has the tag. Hazel finalizes. */
  confirmationV2Body: string;
  /**
   * Pre-filled email subject for the V2 mailto: link.
   * Plain text — encodeURIComponent() applied at render time.
   * Optional: falls back to bare mailto:mail@pianofantasy.com if absent.
   * Hazel finalizes per-instance strings; stored in content/freebies/<slug>.json.
   */
  resendSubject?: string;
  /** Route slug of /free-pdf-downloads/[slug] — GA4 generate_lead `freebie_slug`. */
  freebieSlug: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Clipboard copy state labels — "Copy email" / "Copy subject" fallback buttons
// on the V2 confirmation card. When clipboard write succeeds, the label flips
// to "Copied ✓" for 1800ms then reverts. On Clipboard API failure (e.g.,
// insecure context), shows inline fallback text instead.
// ---------------------------------------------------------------------------
type CopyState = 'idle' | 'copied' | 'failed';

export default function FreebieForm({ ghlTag, confirmationV1Body, confirmationV2Body, resendSubject, freebieSlug }: FreebieFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const formRenderTimeRef = useRef<number>(Date.now());
  const emailRef = useRef<string>('');

  const [step, setStep] = useState<'email' | 'name' | 'done-v1' | 'done-v2' | 'error'>('email');
  const [copyEmailState, setCopyEmailState] = useState<CopyState>('idle');
  const [copySubjectState, setCopySubjectState] = useState<CopyState>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Track whether reduced motion is preferred — read once on mount
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false,
  );

  // Mailcheck layer — same as newsletter form (Layer 1)
  const { checkOnSubmit: mailcheckOnSubmit } = useMailcheck({
    emailInputId: 'fb-email',
    suggestionDivId: 'fb-email-suggestion',
  });

  // Cycle 89: Turnstile invisible CAPTCHA for the freebie form.
  const { containerRef: turnstileRef, getToken: getTurnstileToken, resetWidget: resetTurnstile } = useTurnstile();

  const mailcheckRef = useRef(mailcheckOnSubmit);
  useEffect(() => { mailcheckRef.current = mailcheckOnSubmit; });

  // Card container animation utility
  const animateOpacity = (
    el: HTMLElement,
    from: number,
    to: number,
    durationMs: number,
    callback?: () => void,
  ) => {
    if (prefersReducedMotion.current) {
      el.style.opacity = String(to);
      callback?.();
      return;
    }
    el.style.transition = `opacity ${durationMs}ms cubic-bezier(0.0, 0, 0.2, 1)`;
    el.style.opacity = String(from);
    // requestAnimationFrame to trigger the transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.opacity = String(to);
        if (callback) {
          setTimeout(callback, durationMs);
        }
      });
    });
  };

  // Step field slide animation
  const slideField = (
    el: HTMLElement,
    fromY: number,
    toY: number,
    durationMs: number,
    delayMs: number,
  ) => {
    if (prefersReducedMotion.current) {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
      return;
    }
    el.style.transition = 'none';
    el.style.opacity = fromY === 0 ? '1' : '0';
    el.style.transform = `translateY(${fromY}px)`;
    setTimeout(() => {
      el.style.transition = `transform ${durationMs}ms cubic-bezier(0.0, 0, 0.2, 1), opacity ${durationMs}ms cubic-bezier(0.0, 0, 0.2, 1)`;
      el.style.opacity = '1';
      el.style.transform = `translateY(${toY}px)`;
    }, delayMs);
  };

  // ---------------------------------------------------------------------------
  // Clipboard copy handler — used for V2 copy-email / copy-subject buttons
  // Writes the given value to the clipboard, updates the state label for 1800ms,
  // then reverts. On Clipboard API failure, flips to 'failed' state.
  // ---------------------------------------------------------------------------
  const handleCopy = async (
    value: string,
    setState: React.Dispatch<React.SetStateAction<CopyState>>,
  ) => {
    if (!navigator.clipboard) {
      setState('failed');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      setState('copied');
      setTimeout(() => setState('idle'), 1800);
    } catch {
      setState('failed');
    }
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;

    // Bot timing check — bots submit instantly
    if (Date.now() - formRenderTimeRef.current < 2000) return;

    const emailInput = form.querySelector<HTMLInputElement>('#fb-email');
    if (!emailInput?.checkValidity()) { emailInput?.reportValidity(); return; }

    // Honeypot check
    const honeypot = form.querySelector<HTMLInputElement>('.hp-field input');
    if (honeypot && honeypot.value.length > 0) return;

    // Mailcheck gate
    if (mailcheckRef.current && mailcheckRef.current()) return;

    emailRef.current = emailInput.value.trim();

    // Animate email field exit, then show name field
    const emailState = form.querySelector<HTMLElement>('.fb-state[data-step="email"]');
    const nameState = form.querySelector<HTMLElement>('.fb-state[data-step="name"]');
    if (!emailState || !nameState) return;

    if (!prefersReducedMotion.current) {
      emailState.style.transition = 'transform 300ms cubic-bezier(0.0, 0, 0.2, 1), opacity 300ms cubic-bezier(0.0, 0, 0.2, 1)';
      emailState.style.opacity = '0';
      emailState.style.transform = 'translateY(-8px)';
      setTimeout(() => {
        emailState.style.display = 'none';
        nameState.style.display = 'flex';
        slideField(nameState, 8, 0, 300, 60);
        setTimeout(() => {
          nameState.querySelector<HTMLInputElement>('input')?.focus();
        }, 60 + 100);
      }, 300);
    } else {
      emailState.style.display = 'none';
      nameState.style.display = 'flex';
      nameState.style.opacity = '1';
      nameState.style.transform = 'translateY(0)';
      nameState.querySelector<HTMLInputElement>('input')?.focus();
    }

    setStep('name');
  };

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;

    const nameInput = form.querySelector<HTMLInputElement>('#fb-name');
    if (!nameInput?.checkValidity()) { nameInput?.reportValidity(); return; }

    const firstName = nameInput.value.trim();
    const email = emailRef.current;
    const honeypot = form.querySelector<HTMLInputElement>('.hp-field input');
    const honeypotValue = honeypot?.value ?? '';

    setSubmitError('');
    setIsSubmitting(true);

    // Cycle 89: include Turnstile token in the POST body.
    const turnstileToken = getTurnstileToken();

    try {
      const res = await fetch('/api/freebie', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          firstName,
          ghlTag,
          website: honeypotValue,
          ...(turnstileToken ? { turnstileToken } : {}),
        }),
      });
      const data = await res.json() as { status: string; message?: string };

      if (data.status === 'freebie-sent') {
        // GA4 E2 (Cadence spec 2026-10-27): new free-PDF lead; never on already-has-freebie.
        ga4Event('generate_lead', { lead_type: 'free_pdf', form_source: 'freebie', freebie_slug: freebieSlug });
        // Cycle 89: reset Turnstile after successful submit (tokens are single-use).
        resetTurnstile();
        // Fade card out then set done-v1 state
        const card = form.closest('.freebie-form-card');
        if (card instanceof HTMLElement) {
          animateOpacity(card, 1, 0, 400, () => {
            setStep('done-v1');
            animateOpacity(card, 0, 1, 400);
          });
        } else {
          setStep('done-v1');
        }
      } else if (data.status === 'already-has-freebie') {
        const card = form.closest('.freebie-form-card');
        if (card instanceof HTMLElement) {
          animateOpacity(card, 1, 0, 400, () => {
            setStep('done-v2');
            animateOpacity(card, 0, 1, 400);
          });
        } else {
          setStep('done-v2');
        }
      } else {
        setSubmitError(data.message ?? 'Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Unable to connect. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = (e: React.MouseEvent) => {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;

    const emailState = form.querySelector<HTMLElement>('.fb-state[data-step="email"]');
    const nameState = form.querySelector<HTMLElement>('.fb-state[data-step="name"]');
    if (!emailState || !nameState) return;

    nameState.style.display = 'none';
    emailState.style.display = 'flex';
    if (!prefersReducedMotion.current) {
      emailState.style.opacity = '0';
      emailState.style.transform = 'translateY(8px)';
      slideField(emailState, 8, 0, 300, 0);
    } else {
      emailState.style.opacity = '1';
      emailState.style.transform = 'translateY(0)';
    }
    emailState.querySelector<HTMLInputElement>('input')?.focus();
    setStep('email');
    setSubmitError('');
    // Give 3s head-start so returning user isn't blocked by bot-timing gate
    formRenderTimeRef.current = Date.now() - 3000;
  };

  // ---------------------------------------------------------------------------
  // Confirmation cards (V1 + V2)
  // ---------------------------------------------------------------------------

  if (step === 'done-v1') {
    return (
      <div className="freebie-confirm-card" role="status" aria-live="polite">
        <div className="freebie-confirm-icon" aria-hidden="true" style={{ color: 'var(--gold)' }}>
          <EnvelopeIcon />
        </div>
        <p className="freebie-confirm-heading">Check your inbox.</p>
        <p className="freebie-confirm-body">{confirmationV1Body}</p>
      </div>
    );
  }

  if (step === 'done-v2') {
    // Plain subject for clipboard — decoded from encodeURIComponent to human-readable string
    const plainSubject = resendSubject ?? '';

    return (
      <div className="freebie-confirm-card" role="status" aria-live="polite">
        <div className="freebie-confirm-icon" aria-hidden="true" style={{ color: 'var(--gold)' }}>
          <PersonIcon />
        </div>
        <p className="freebie-confirm-heading">Looks like you already have it.</p>
        <p className="freebie-confirm-body">{renderWithEmailLink(confirmationV2Body, resendSubject)}</p>
        {/*
          Copy-email / copy-subject fallback row — cycle 15 Change A.
          For visitors whose default mail handler is webmail-in-browser (Gmail),
          the mailto: link silently fails. These ghost buttons let them copy the
          address and subject to clipboard so they can paste into Gmail manually.
          Visual spec: 13px, secondary ghost, no CTA-register — must NOT compete
          with the primary form hierarchy.
          Accessibility: aria-label on each button, focus ring via --focus-ring.
        */}
        <div className="freebie-copy-fallback">
          <p className="freebie-copy-fallback-hint">
            If the link above doesn&apos;t open in your email program:
          </p>
          <div className="freebie-copy-fallback-btns">
            {copyEmailState === 'failed' ? (
              <span className="freebie-copy-fallback-fail">
                Copy failed — please long-press the email above
              </span>
            ) : (
              <button
                type="button"
                className="freebie-copy-btn"
                aria-label="Copy email address to clipboard"
                onClick={() => handleCopy(RESEND_EMAIL, setCopyEmailState)}
              >
                {copyEmailState === 'copied' ? 'Copied ✓' : 'Copy email'}
              </button>
            )}
            {plainSubject && (
              <>
                {copySubjectState === 'failed' ? (
                  <span className="freebie-copy-fallback-fail">
                    Copy failed — please type the subject manually
                  </span>
                ) : (
                  <button
                    type="button"
                    className="freebie-copy-btn"
                    aria-label="Copy email subject to clipboard"
                    onClick={() => handleCopy(plainSubject, setCopySubjectState)}
                  >
                    {copySubjectState === 'copied' ? 'Copied ✓' : 'Copy subject'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Form (step email → name)
  // ---------------------------------------------------------------------------

  return (
    <form
      ref={formRef}
      onSubmit={step === 'email' ? handleEmailSubmit : handleNameSubmit}
      noValidate
      className="freebie-form"
      aria-label="Get the free PDF"
    >
      {/* Honeypot — hidden from humans, catches bots */}
      <div className="hp-field" aria-hidden="true" style={{ display: 'none' }}>
        <input type="text" name="website" tabIndex={-1} autoComplete="off" />
      </div>

      {/* Step 1 — email */}
      <div
        className="fb-state"
        data-step="email"
        style={{ display: step !== 'name' ? 'flex' : 'none', flexDirection: 'column', gap: 'var(--space-12)' }}
      >
        <label htmlFor="fb-email" className="sr-only">Email address</label>
        <input
          id="fb-email"
          type="email"
          name="email"
          autoComplete="email"
          required
          placeholder="Enter your email address"
          className="freebie-field"
          aria-required="true"
          aria-describedby="fb-email-suggestion"
        />
        {/* Mailcheck suggestion div — aria-live="polite" per useMailcheck convention */}
        <div id="fb-email-suggestion" role="alert" aria-live="polite" />
        <button
          type="submit"
          className="freebie-cta-btn btn-primary"
          aria-label="Get the free PDF"
        >
          Get the Free PDF
        </button>
      </div>

      {/* Cycle 89: Turnstile invisible widget — off-screen, initialises while user fills the form */}
      <div
        ref={turnstileRef}
        aria-hidden="true"
        style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}
      />

      {/* Step 2 — name */}
      <div
        className="fb-state"
        data-step="name"
        style={{
          display: step === 'name' ? 'flex' : 'none',
          flexDirection: 'column',
          gap: 'var(--space-12)',
          opacity: 0,
          transform: 'translateY(8px)',
        }}
      >
        {/* Step 2 heading — matches NewsletterForm.tsx line 110 exactly */}
        <h3>What&apos;s your first name?</h3>
        <label htmlFor="fb-name" className="sr-only">First name</label>
        <input
          id="fb-name"
          type="text"
          name="firstName"
          autoComplete="given-name"
          required
          placeholder="Your first name"
          className="freebie-field"
          aria-required="true"
        />
        {submitError && (
          <p className="freebie-error" role="alert" aria-live="assertive">
            {submitError}
          </p>
        )}
        <button
          type="submit"
          className="freebie-cta-btn btn-primary"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
          aria-label="Send me the PDF"
        >
          {isSubmitting ? (
            <span className="lv-inline-loading">
              <OrbitSpinner size={16} aria-label="Sending" />
              Sending…
            </span>
          ) : 'Send Me the PDF'}
        </button>
        {/* Back link — returns to email step without clearing email */}
        <div style={{ textAlign: 'right' }}>
          <button
            type="button"
            onClick={handleBack}
            className="freebie-back-link"
            aria-label="Go back and change email address"
          >
            ← change email
          </button>
        </div>
      </div>
    </form>
  );
}
